import { BATTLE_MODES, TURN_SIDES } from "../../game/core/constants.js";
import { getCommanderById } from "../../game/content/commanders.js";
import {
  canActivatePlayerPower,
  canSelectNextReadyUnit,
  isPlayerPowerCharged,
  renderCommanderPanel
} from "./battleHud/commanderPanels.js";
import {
  renderActionPrompt,
  renderCommandFeed,
  renderExtinguishPrompt,
  renderMedpackPrompt,
  renderRecruitPanel,
  renderSupportPrompt,
  renderTargetingPrompt,
  renderTransportPrompt,
  renderUnloadPrompt
} from "./battleHud/interactionPanels.js";
import {
  renderBattleNotice,
  renderLevelUpOverlay,
  renderOutcomeOverlay,
  renderPauseOverlay,
  renderPowerOverlay,
  renderRunCardsOverlay,
  renderTurnBanner
} from "./battleHud/overlays.js";
import { renderCombatCutsceneOverlay } from "./battleHud/combatCutsceneOverlay.js";
import { renderTutorialGuide } from "./battleHud/tutorialGuide.js";
import {
  getFocusTileForSide,
  renderSelectionDetails,
  renderTargetIntelPanel
} from "./battleHud/selectionPanels.js";

const BATTLE_CHROME_GLOW_CYCLE_MS = 4200;
const BATTLE_CHROME_SWEEP_CYCLE_MS = 6800;
const BATTLE_FOOTER_BUTTON_IMAGE_DIMENSIONS = {
  "end-turn": { width: 612, height: 408 },
  next: { width: 612, height: 408 },
  pause: { width: 612, height: 408 }
};
const DEFAULT_BATTLE_FOOTER_BUTTON_IMAGE_DIMENSIONS = {
  width: 866,
  height: 288
};

function getCommanderTurnState(activeSide, side) {
  if (!activeSide) {
    return "neutral";
  }

  return activeSide === side ? "active" : "inactive";
}

function getBattleChromeStyle(animationClockMs) {
  const animationClock = Number.isFinite(animationClockMs) ? animationClockMs : 0;

  return [
    `--battle-chrome-glow-duration:${BATTLE_CHROME_GLOW_CYCLE_MS}ms`,
    `--battle-chrome-sweep-duration:${BATTLE_CHROME_SWEEP_CYCLE_MS}ms`,
    `--battle-chrome-glow-delay:${-Math.floor(animationClock % BATTLE_CHROME_GLOW_CYCLE_MS)}ms`,
    `--battle-chrome-sweep-delay:${-Math.floor(animationClock % BATTLE_CHROME_SWEEP_CYCLE_MS)}ms`
  ].join("; ");
}

function renderBattleMeta(battleSnapshot) {
  const mapName = battleSnapshot.map?.name ?? "Unknown Map";
  const turnLabel = battleSnapshot.turn?.number ?? 1;
  const mission = battleSnapshot.presentation?.mission ?? null;

  return `
    <div class="battle-footer-meta" aria-label="Battle mission details">
      <span class="battle-footer-meta__item">
        <strong>Mission</strong>
        <em>${mission?.label ?? "Rout"}</em>
        ${mission?.status ? `<small>${mission.status}</small>` : ""}
      </span>
      <span class="battle-footer-meta__item">
        <strong>Map</strong>
        <em>${mapName}</em>
      </span>
      <span class="battle-footer-meta__item">
        <strong>Turn</strong>
        <em>${turnLabel}</em>
      </span>
    </div>
  `;
}

function renderBattleFooterImageButton({
  action,
  className,
  label,
  imageSlug,
  disabled = false
}) {
  const imageUrl = `./assets/img/ui/buttons/${imageSlug}.png`;
  const imageDimensions = BATTLE_FOOTER_BUTTON_IMAGE_DIMENSIONS[imageSlug] ??
    DEFAULT_BATTLE_FOOTER_BUTTON_IMAGE_DIMENSIONS;

  return `
    <button
      class="menu-button battle-footer-button ${className} title-button--has-image title-button--image-loaded"
      data-action="${action}"
      aria-label="${label}"
      ${disabled ? "disabled" : ""}
    >
      <img
        class="title-button__image"
        src="${imageUrl}"
        width="${imageDimensions.width}"
        height="${imageDimensions.height}"
        alt=""
        aria-hidden="true"
        loading="eager"
        decoding="async"
        onload="this.closest('button')?.classList.add('title-button--image-loaded')"
        onerror="const button=this.closest('button'); button?.classList.remove('title-button--image-loaded'); this.remove()"
      />
    </button>
  `;
}

function renderCompactIntelSheet(
  battleSnapshot,
  sidebarBattleSnapshot,
  playerFocusTile,
  hoveredTile,
  enemyFocusTile,
  experiencePresentation = null
) {
  return `
    <input
      class="battle-intel-tab-toggle"
      id="battle-intel-tab-selected"
      name="battle-intel-tab"
      type="radio"
      value="selected"
      checked
      aria-hidden="true"
    />
    <input
      class="battle-intel-tab-toggle"
      id="battle-intel-tab-target"
      name="battle-intel-tab"
      type="radio"
      value="target"
      aria-hidden="true"
    />
    <input
      class="battle-intel-tab-toggle"
      id="battle-intel-tab-feed"
      name="battle-intel-tab"
      type="radio"
      value="feed"
      aria-hidden="true"
    />
    <aside class="battle-compact-sheet" aria-label="Battle Intel">
      <div class="battle-drawer-header battle-drawer-header--compact">
        <span>Intel</span>
        <label class="ghost-button ghost-button--small" for="battle-intel-drawer">Close</label>
      </div>
      <div class="battle-compact-sheet__tabs" role="tablist" aria-label="Battle Intel Tabs">
        <label class="battle-compact-sheet__tab" for="battle-intel-tab-selected">Selected Unit</label>
        <label class="battle-compact-sheet__tab" for="battle-intel-tab-target">Target Intel</label>
        <label class="battle-compact-sheet__tab" for="battle-intel-tab-feed">Command Feed</label>
      </div>
      <div class="battle-compact-sheet__panels">
        <section class="battle-compact-sheet__panel battle-compact-sheet__panel--selected">
          ${renderSelectionDetails(playerFocusTile, {
            title: "Selected Unit",
            emptyTitle: "Selected Unit",
            emptyBody: "Select a friendly unit, building, or tile to inspect it here.",
            experiencePresentation
          })}
          ${renderRecruitPanel(battleSnapshot)}
        </section>
        <section class="battle-compact-sheet__panel battle-compact-sheet__panel--target">
          ${renderTargetIntelPanel(sidebarBattleSnapshot, hoveredTile, enemyFocusTile)}
        </section>
        <section class="battle-compact-sheet__panel battle-compact-sheet__panel--feed">
          ${renderCommandFeed(battleSnapshot.log, hoveredTile)}
        </section>
      </div>
    </aside>
  `;
}

function renderDesktopBattlePanels(
  battleSnapshot,
  sidebarBattleSnapshot,
  hoveredTile,
  playerFocusTile,
  enemyFocusTile,
  experiencePresentation = null
) {
  return `
    <div class="battle-desktop-layout">
      <div class="battle-side-panel-shell battle-side-panel-shell--selected">
        <aside class="battle-side-panel battle-side-panel--selected" aria-label="Selected Unit Intel">
          ${renderSelectionDetails(playerFocusTile, {
            title: "Selected Unit",
            emptyTitle: "Selected Unit",
            emptyBody: "Select a friendly unit, building, or tile to inspect it here.",
            experiencePresentation
          })}
          ${renderRecruitPanel(battleSnapshot)}
        </aside>
      </div>
      <div class="battle-side-stack battle-side-stack--right" aria-label="Target Intel and Command Feed">
        <div class="battle-side-panel-shell battle-side-panel-shell--target">
          <aside class="battle-side-panel battle-side-panel--target" aria-label="Target Intel">
            ${renderTargetIntelPanel(sidebarBattleSnapshot, hoveredTile, enemyFocusTile)}
          </aside>
        </div>
        <div class="battle-side-panel-shell battle-side-panel-shell--feed">
          <aside class="battle-side-panel battle-side-panel--feed" aria-label="Command Feed">
            ${renderCommandFeed(battleSnapshot.log, hoveredTile)}
          </aside>
        </div>
      </div>
    </div>
  `;
}

function isCommanderPowerActiveForSide(battleSnapshot, side) {
  const turnNumber = Number(battleSnapshot?.turn?.number);
  const activeSide = battleSnapshot?.turn?.activeSide;
  const powerUsedTurn = Number(battleSnapshot?.[side]?.powerUsedTurn);

  if (!Number.isFinite(turnNumber) || !Number.isFinite(powerUsedTurn) || !activeSide) {
    return false;
  }

  if (activeSide === side) {
    return powerUsedTurn === turnNumber;
  }

  if (side === TURN_SIDES.PLAYER && activeSide === TURN_SIDES.ENEMY) {
    return powerUsedTurn === turnNumber - 1;
  }

  if (side === TURN_SIDES.ENEMY && activeSide === TURN_SIDES.PLAYER) {
    return powerUsedTurn === turnNumber;
  }

  return false;
}

export function renderBattleHudView(state, options = {}) {
  const battleSnapshot = state.battleSnapshot;
  const suppressLevelUpOverlay = options.suppressLevelUpOverlay ?? false;
  const suppressOutcomeOverlay = options.suppressOutcomeOverlay ?? false;
  const turnBanner = options.turnBanner ?? null;
  const combatCutscene = state.battleUi?.combatCutscene ?? null;
  const combatCutsceneActive = Boolean(combatCutscene);
  const sidebarBattleSnapshot = combatCutscene?.hudSnapshot ?? battleSnapshot;
  const experiencePresentation = options.experiencePresentation ?? null;
  const levelUpPresentation = options.levelUpPresentation ?? null;
  const commanderTurnAnimationFromSide = options.commanderTurnAnimationFromSide ?? null;
  const displayContext = options.displayContext ?? {};

  if (!battleSnapshot) {
    return "";
  }

  const playerCommander = getCommanderById(battleSnapshot.player.commanderId);
  const enemyCommander = getCommanderById(battleSnapshot.enemy.commanderId);
  const nextUnitEnabled = canSelectNextReadyUnit(battleSnapshot);
  const playerPowerEnabled = canActivatePlayerPower(battleSnapshot);
  const playerPowerCharged = isPlayerPowerCharged(battleSnapshot);
  const fundsGain = state.battleUi?.fundsGain ?? null;
  const hoveredTile = state.battleUi?.hoveredTile ?? null;
  const showFunds = battleSnapshot.mode !== BATTLE_MODES.RUN;
  const playerPowerActive = isCommanderPowerActiveForSide(battleSnapshot, TURN_SIDES.PLAYER);
  const enemyPowerActive = isCommanderPowerActiveForSide(battleSnapshot, TURN_SIDES.ENEMY);
  const activeSide = battleSnapshot?.turn?.activeSide;
  const playerTurnState = getCommanderTurnState(activeSide, TURN_SIDES.PLAYER);
  const enemyTurnState = getCommanderTurnState(activeSide, TURN_SIDES.ENEMY);
  const playerTurnAnimationFromState = commanderTurnAnimationFromSide
    ? getCommanderTurnState(commanderTurnAnimationFromSide, TURN_SIDES.PLAYER)
    : null;
  const enemyTurnAnimationFromState = commanderTurnAnimationFromSide
    ? getCommanderTurnState(commanderTurnAnimationFromSide, TURN_SIDES.ENEMY)
    : null;
  const commanderAnimationClockMs =
    typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now();
  const battleChromeStyle = getBattleChromeStyle(commanderAnimationClockMs);
  const playerFocusTile = getFocusTileForSide(sidebarBattleSnapshot, state.battleUi, TURN_SIDES.PLAYER);
  const enemyFocusTile = getFocusTileForSide(sidebarBattleSnapshot, state.battleUi, TURN_SIDES.ENEMY);

  return `
    <div class="battle-shell battle-shell--animated-chrome" style="${battleChromeStyle}">
      <input class="battle-drawer-toggle" id="battle-intel-drawer" type="checkbox" aria-hidden="true" />
      <input class="battle-drawer-toggle" id="battle-command-drawer" type="checkbox" aria-hidden="true" />
      <div class="battle-commanders">
        ${renderCommanderPanel(playerCommander, battleSnapshot.player, "player", {
          turnState: playerTurnState,
          turnAnimationFromState: playerTurnAnimationFromState,
          fundsGain,
          showFunds,
          canActivatePower: playerPowerEnabled,
          isCharged: playerPowerCharged,
          isActive: playerPowerActive,
          animationClockMs: commanderAnimationClockMs
        })}
        ${renderCommanderPanel(enemyCommander, battleSnapshot.enemy, "enemy", {
          turnState: enemyTurnState,
          turnAnimationFromState: enemyTurnAnimationFromState,
          fundsGain,
          showFunds,
          isActive: enemyPowerActive,
          animationClockMs: commanderAnimationClockMs
        })}
      </div>
      <div class="battle-footer-actions" aria-label="Battle controls">
        <label
          class="ghost-button ghost-button--small battle-footer-button battle-footer-button--intel battle-drawer-button"
          for="battle-intel-drawer"
        >
          Intel
        </label>
        <button
          class="ghost-button ghost-button--small battle-footer-button battle-footer-button--cards"
          data-action="open-run-cards"
        >
          Cards
        </button>
        ${renderBattleFooterImageButton({
          action: "pause-battle",
          className: "battle-footer-button--pause",
          label: "Pause",
          imageSlug: "pause"
        })}
        ${renderBattleFooterImageButton({
          action: "select-next-unit",
          className: "battle-footer-button--next",
          label: "Next",
          imageSlug: "next",
          disabled: !nextUnitEnabled
        })}
        ${renderBattleFooterImageButton({
          action: "end-turn",
          className: "battle-footer-button--end-turn",
          label: "End Turn",
          imageSlug: "end-turn"
        })}
        <label
          class="ghost-button ghost-button--small battle-footer-button battle-footer-button--feed battle-drawer-button"
          for="battle-command-drawer"
        >
          Feed
        </label>
      </div>
      ${renderBattleMeta(battleSnapshot)}
      ${renderDesktopBattlePanels(
        battleSnapshot,
        sidebarBattleSnapshot,
        hoveredTile,
        playerFocusTile,
        enemyFocusTile,
        experiencePresentation
      )}
      ${renderCompactIntelSheet(
        battleSnapshot,
        sidebarBattleSnapshot,
        playerFocusTile,
        hoveredTile,
        enemyFocusTile,
        experiencePresentation
      )}
      ${renderActionPrompt(battleSnapshot)}
      ${renderTargetingPrompt(battleSnapshot)}
      ${renderUnloadPrompt(battleSnapshot)}
      ${renderTransportPrompt(battleSnapshot)}
      ${renderSupportPrompt(battleSnapshot)}
      ${renderMedpackPrompt(battleSnapshot)}
      ${renderExtinguishPrompt(battleSnapshot)}
      ${renderTutorialGuide(battleSnapshot)}
      ${combatCutsceneActive ? "" : renderBattleNotice(state.battleUi?.notice)}
      ${combatCutsceneActive ? "" : renderTurnBanner(turnBanner)}
      ${combatCutsceneActive ? "" : renderPowerOverlay(state.battleUi?.powerOverlay)}
      ${renderCombatCutsceneOverlay(combatCutscene, state.metaState?.options)}
      ${combatCutsceneActive ? "" : renderRunCardsOverlay(state, battleSnapshot)}
      ${
        suppressLevelUpOverlay || combatCutsceneActive
          ? ""
          : renderLevelUpOverlay(
              battleSnapshot,
              levelUpPresentation,
              state.metaState?.options
            )
      }
      ${combatCutsceneActive ? "" : renderPauseOverlay(state, battleSnapshot, displayContext)}
      ${suppressOutcomeOverlay || combatCutsceneActive ? "" : renderOutcomeOverlay(state, battleSnapshot)}
    </div>
  `;
}
